import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { IErrorMessage, useErrorMessage } from '@hooks/use-error-message.ts';
import { BackIcon } from '@components/icons/svg-icon.tsx';
import { handleBack } from '@utils/navigation.utils.ts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import TimeEstimateInput from '@components/time-estimate-input/time-estimate-input.tsx';
import ColorInput from '@components/color-input/color-input.tsx';
import AppOutlinedField from '@components/app-outlined-field/app-outlined-field.tsx';
import AppSelectField from '@components/app-select-field/app-select-field.tsx';
import AppSwitchField from '@components/app-switch-field/app-switch-field.tsx';

function InputField() {
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    trigger
  } = useForm<any>({ mode: 'onTouched' });
  const [switchOn, setSwitchOn] = useState<boolean>(true);
  const [country, setCountry] = useState<string>('');
  const getErrorMessage = useErrorMessage();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [brandingColor, setBrandingColor] = useState<string>('#8d6cd5');
  const [brandingValue, setBrandingValue] = useState<string>(brandingColor);
  const brandingColorInputRef = useRef<HTMLInputElement>(null);

  const errorMessages: IErrorMessage = {
    email: {
      required: 'validation_email_required',
      pattern: 'validation_email_pattern'
    },
    password: {
      required: 'validation_password_required',
      maxLength: 'validation_password_maxlength',
      minLength: 'validation_password_minlength',
      max: 'validation_password_invalid_code'
    },
    age: {
      required: 'validation_email_required'
    },
    date: {
      required: 'validation_date_required'
    },
    duration: {
      required: 'validation_duration_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    }
  };

  const onSubmit = () => {};

  return (
    <div>
      <div className="mb-12">
        <IconButton
          disableRipple={true}
          className="no-hover"
          onClick={() => handleBack(navigate)}
        >
          <div className="flex items-center gap-2 ml-3">
            <BackIcon />
            <Typography variant="body1" className="max-lg-xl:text-[14px]!">
              {t('back')}
            </Typography>
          </div>
        </IconButton>
      </div>

      <form className="flex justify-between gap-4">
        <div className="flex flex-col gap-1 w-1/2">
          <FormControl error={!!errors.email}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span> {t('label_email')}
            </FormLabel>
            <OutlinedInput
              placeholder="Enter your email"
              {...register('email', {
                required: true,
                pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
              })}
              error={!!errors.email}
            />
            <FormHelperText>
              {errors?.email &&
                getErrorMessage({
                  errors,
                  fieldName: 'email',
                  errorMessages
                })}
            </FormHelperText>
          </FormControl>

          <FormControl error={!!errors.password} sx={{ minWidth: 320 }}>
            <FormLabel className="flex">{t('label_password')}</FormLabel>
            <OutlinedInput
              type="password"
              placeholder="Enter your password"
              {...register('password', {
                required: true,
                minLength: 6,
                maxLength: 10
              })}
              error={!!errors.password}
            />
            <FormHelperText>
              {errors?.password &&
                getErrorMessage({
                  errors,
                  fieldName: 'password',
                  errorMessages,
                  validationRules: {
                    minLength: 6,
                    maxLength: 10
                  }
                })}
            </FormHelperText>
          </FormControl>

          <FormControl sx={{ minWidth: 320 }} error={!!errors.age}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span> Age
            </FormLabel>
            <Select
              displayEmpty
              {...register('age', {
                required: true
              })}
              error={!!errors.age}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={10}>10 years old</MenuItem>
              <MenuItem value={20}>20 years old</MenuItem>
              <MenuItem value={30}>30 years old</MenuItem>
            </Select>
            <FormHelperText>
              {errors?.age &&
                getErrorMessage({
                  errors,
                  fieldName: 'age',
                  errorMessages
                })}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Notes</FormLabel>
            <OutlinedInput multiline rows={4} placeholder="Enter your notes" />
          </FormControl>

          <Button
            onClick={handleSubmit(onSubmit)}
            sx={{ marginTop: '20px' }}
            type="submit"
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </div>

        <div className="w-1/2">
          <div>
            <p className="text-xs text-gray-500 mt-1 mb-7">
              🟡 Enter the duration in numbers. Examples: <br />•{' '}
              <strong>2</strong> → 2 hours <br />• <strong>30</strong> → 30
              minutes <br />• <strong>2.3</strong> → 2 hours 18 minutes <br />
              You can enter hours, minutes, or decimals — it will convert
              automatically.
            </p>

            <TimeEstimateInput
              name="duration"
              label={
                <span className="flex items-center gap-1">
                  <span className="reuqired-symbole">*</span>
                  {t('label_duration')}
                  <InfoTooltip text={t('tooltip_info_duration')} />
                </span>
              }
              placeholder={t('placeholder_duration')}
              control={control}
              validationProps={{
                errors,
                errorMessages,
                getErrorMessage,
                validationRules: {
                  required: true
                }
              }}
            />
          </div>

          <FormControl className="w-full" error={!!errors.date}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_date')} <InfoTooltip text="tooltip_info_date" />
            </FormLabel>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="date"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    onClose={() => {
                      trigger('date');
                    }}
                    slotProps={{
                      textField: {
                        error: !!errors.date,
                        placeholder: t(
                          'new_reservation_details_form_date_of_birth_placeholder'
                        ),
                        onBlur: async (event: any) => {
                          if (event.relatedTarget?.closest('[role="dialog"]'))
                            return;

                          field.onBlur();
                          await trigger('date');
                        }
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            <FormHelperText>
              {errors?.date &&
                getErrorMessage({
                  errors,
                  fieldName: 'date',
                  errorMessages
                })}
            </FormHelperText>
          </FormControl>

          <FormControl style={{ width: '100%', marginTop: '-5px' }}>
            <ColorInput
              label="partners_branding_color"
              color={brandingColor}
              setColor={setBrandingColor}
              textValue={brandingValue}
              setTextValue={setBrandingValue}
              currentRef={brandingColorInputRef}
            />
          </FormControl>
        </div>
      </form>

      {/* Shared field wrappers */}
      <div className="mt-14 flex flex-col gap-6">
        <h3 className="text-lg font-bold text-gray-800">
          Shared field wrappers
        </h3>

        <div className="flex flex-wrap gap-8">
          <div className="w-full max-w-sm flex flex-col gap-6">
            <p className="font-medium text-gray-700">AppOutlinedField</p>
            <AppOutlinedField
              label="Default"
              placeholder="Enter value"
              helperText="Optional helper text"
            />
            <AppOutlinedField
              label="Error state"
              placeholder="Enter value"
              error
              helperText="This field is required"
            />
            <AppOutlinedField
              label="Disabled"
              placeholder="Disabled"
              disabled
            />
          </div>

          <div className="w-full max-w-sm flex flex-col gap-6">
            <p className="font-medium text-gray-700">AppSelectField</p>
            <AppSelectField
              label="Country"
              displayEmpty
              value={country}
              onChange={(e) => setCountry(e.target.value as string)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="us">United States</MenuItem>
              <MenuItem value="ae">United Arab Emirates</MenuItem>
              <MenuItem value="am">Armenia</MenuItem>
            </AppSelectField>
            <AppSelectField label="Disabled" value="us" disabled>
              <MenuItem value="us">United States</MenuItem>
            </AppSelectField>
          </div>

          <div className="w-full max-w-sm flex flex-col gap-6">
            <p className="font-medium text-gray-700">AppSwitchField</p>
            <AppSwitchField
              label="Enable feature"
              checked={switchOn}
              onChange={setSwitchOn}
            />
            <AppSwitchField
              label="Always off"
              checked={false}
              onChange={() => {}}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default InputField;
